/**
 * Tennessee Home Care Organization (Home Health Services) Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: Chapter 0720-27 (Standards for Home Care Organizations Providing Home Health Services)
 *   Transferred from 1200-08-26 effective July 1, 2022 per Public Chapter 1119 of 2022
 * - Statutory Authority: TCA §§ 68-11-201 et seq.
 * - Criminal Background Check: TCA § 68-11-233 (home care specific)
 * - Adult Abuse Reporting: TCA § 71-6-103 (Adult Protection Act)
 * - Abuse Registry: TCA § 68-11-1001 et seq.
 * 
 * REGULATORY BODY:
 * Tennessee Health Facilities Commission (THFC)
 * 665 Mainstream Drive
 * Nashville, TN 37243
 * Licensing: (615) 741-7221
 * Complaints: (877) 287-0010
 * Website: https://www.tn.gov/healthfacilities
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - Annual license fee: $1,404 per 0720-27-.02(2)(b)
 * - License expires annually on anniversary date per 0720-27-.02(1)
 * - Record retention: 10 years minimum per 0720-27-.06(11)(c)
 * - Plan of care review: Every 62 days per 0720-27-.06(3)(b)
 * - Summary report to physician: Every 62 days per 0720-27-.06(2)
 * - HHA training: 75 hours total (16 clinical) per 0720-27-.01(29)
 * - HHA supervision: Monthly on-site per 0720-27-.06(7)(c)
 * - Background check required PRIOR to employment per TCA § 68-11-233
 * - Abuse report within 5 business days per 0720-27-.12(1)(d)
 * - APS Hotline: 1-888-APS-TENN (1-888-277-8366)
 * 
 * Last Verified: December 2024 against Chapter 0720-27
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const TENNESSEE_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'TN-A1',
    itemId: 'A1',
    stateCode: 'TN',
    title: 'Home Care Organization License Status',
    description: 'Tennessee requires all home care organizations providing home health services to be licensed by the Tennessee Health Facilities Commission per Chapter 0720-27',
    severity: 'high',
    regulationCode: '0720-27-.02(1)',
    regulationName: 'Licensing Procedures',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Organization license from the Tennessee Health Facilities Commission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 0720-27-.02(1): No entity shall operate a home care organization providing home health services without a license.',
      },
      {
        id: 'q2',
        question: 'What is your HCO license expiration date?',
        inputType: 'date',
        helpText: 'Per 0720-27-.02(1): Licenses expire annually on the anniversary date of original issuance.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is your license conspicuously posted in your home care organization?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.02(1): The license shall be conspicuously posted.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // License not posted per 0720-27-.02(1)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Tennessee Health Facilities Commission Home Care Organization license is current per Chapter 0720-27.',
      atRisk: 'Your HCO license expires soon or is not conspicuously posted. Submit renewal before expiration. Per 0720-27-.02(4)(b), late renewal incurs $100/month penalty. License must be posted per 0720-27-.02(1).',
      nonCompliant: 'Operating without a valid THFC license violates TCA § 68-11-201. Contact THFC immediately at (615) 741-7221.',
      unknown: 'Verify your HCO license status with THFC at (615) 741-7221.',
    },
    stateNotes: [
      'Annual license fee: $1,404 per 0720-27-.02(2)(b)',
      'Licenses expire annually on anniversary date per 0720-27-.02(1)',
      'Late renewal penalty: $100/month, max 2x renewal fee per 0720-27-.02(4)(b)',
      'If renewal lapses 60+ days, must reapply per 0720-27-.02(4)(c)',
      'Licenses not transferable or assignable per 0720-27-.02(1)',
    ],
  },

  {
    id: 'TN-A2',
    itemId: 'A2',
    stateCode: 'TN',
    title: 'Administrator Qualifications',
    description: 'Tennessee requires qualified administrator per 0720-27-.01(2) and 0720-27-.04(8)',
    severity: 'high',
    regulationCode: '0720-27-.01(2), 0720-27-.04(6-9)',
    regulationName: 'Administrator',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet Tennessee qualification requirements?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician with 1+ year supervisory/admin experience in home health/hospice/related programs' },
          { value: 'rn', label: 'RN with 1+ year supervisory/admin experience in home health/hospice/related programs' },
          { value: 'health_admin', label: 'Training/experience in health service admin + 1 year supervisory/admin experience in home health/hospice/related programs' },
          { value: 'none', label: 'Does not meet any qualification' },
        ],
        required: true,
        helpText: 'Per 0720-27-.01(2): Administrator must meet one of these qualifications.',
      },
      {
        id: 'q2',
        question: 'Is the administrator accessible during normal operating hours?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.04(8): Administrator shall be accessible during normal operating hours.',
      },
      {
        id: 'q3',
        question: 'Do you have a written authorization for someone to assume temporary duty during administrator absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.04(7): Person with sufficient experience shall be authorized in writing for short-term absence.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: Unqualified administrator or not accessible
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // AT_RISK: No written authorization for backup
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      // COMPLIANT: Qualified, accessible, with backup authorization
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['physician', 'rn', 'health_admin'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications and availability meet 0720-27-.01(2) and 0720-27-.04(7-8) requirements.',
      atRisk: 'Administrator qualified but missing written backup authorization per 0720-27-.04(7).',
      nonCompliant: 'Administrator does not meet Tennessee qualification or accessibility requirements.',
      unknown: 'Verify administrator qualifications per 0720-27-.01(2).',
    },
    stateNotes: [
      'Administrator change must be reported within 15 days per 0720-27-.04(8)',
      'Administrator may serve only one licensed HCO unless same ownership/location per 0720-27-.04(9)',
      'Written authorization required for backup during absence per 0720-27-.04(7)',
    ],
  },

  {
    id: 'TN-A3',
    itemId: 'A3',
    stateCode: 'TN',
    title: 'Policies and Procedures',
    description: 'Tennessee requires written policies consistent with professional standards per 0720-27-.04',
    severity: 'high',
    regulationCode: '0720-27-.04(15-16)',
    regulationName: 'Administrative Requirements',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Which Tennessee-required policies does your agency have documented?',
        inputType: 'checklist',
        options: [
          { value: 'org_structure', label: 'Organizational structure and lines of authority - 0720-27-.04(5)' },
          { value: 'personnel', label: 'Personnel practices and job descriptions - 0720-27-.04(12)' },
          { value: 'admission', label: 'Admission, discharge, and transfer procedures - 0720-27-.05' },
          { value: 'patient_rights', label: 'Patient rights - 0720-27-.12' },
          { value: 'advance_directive', label: 'Advance directives/health care decision-making - 0720-27-.13' },
          { value: 'infection', label: 'Infection control - 0720-27-.06(10)' },
          { value: 'waste', label: 'Infectious/hazardous waste handling - 0720-27-.10' },
          { value: 'hiv_policy', label: 'HIV testing policies - 0720-27-.04(17)' },
          { value: 'disaster', label: 'Disaster preparedness - 0720-27-.14' },
          { value: 'influenza', label: 'Annual influenza vaccination program - 0720-27-.06(10)(d)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.04(15): Whenever rules require written policies, licensee shall develop, maintain, and adhere to them.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Policy manual meets Tennessee Chapter 0720-27 requirements.',
      atRisk: 'Policy manual may be missing required sections. THFC surveyors will review completeness.',
      nonCompliant: 'Multiple required policies missing. Immediately develop written policies.',
      unknown: 'Review policies against Chapter 0720-27 requirements.',
    },
    stateNotes: [
      'Policies must be consistent with professionally recognized standards per 0720-27-.04(16)',
      'Agency violating its own policy also violates the rule per 0720-27-.04(15)',
      'Administrative functions shall not be delegated to another agency per 0720-27-.04(5)',
    ],
  },

  {
    id: 'TN-A4',
    itemId: 'A4',
    stateCode: 'TN',
    title: 'Performance Improvement Program',
    description: 'Tennessee requires annual performance improvement review per 0720-27-.06(9)',
    severity: 'medium',
    regulationCode: '0720-27-.06(9)',
    regulationName: 'Performance Improvement',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Does your performance improvement committee meet required objectives?',
        inputType: 'checklist',
        options: [
          { value: 'review', label: 'Annual review of past and present services including contracts - 0720-27-.06(9)(a)' },
          { value: 'needs', label: 'Assist in meeting individual and community needs - 0720-27-.06(9)(b)(1)' },
          { value: 'deficiencies', label: 'Identify and correct deficiencies - 0720-27-.06(9)(b)(2)' },
          { value: 'quality', label: 'Critical judgments regarding quality and quantity - 0720-27-.06(9)(b)(3)' },
          { value: 'effectiveness', label: 'Evaluate policy effectiveness - 0720-27-.06(9)(b)(4)' },
          { value: 'criteria', label: 'Establish criteria to measure effectiveness/efficiency - 0720-27-.06(9)(b)(7)' },
          { value: 'record_review', label: 'Record review system for appropriateness of services - 0720-27-.06(9)(b)(8)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(9): Committee shall review services at least annually per written plan.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Performance improvement program meets 0720-27-.06(9) requirements.',
      atRisk: 'Performance improvement program may be incomplete.',
      nonCompliant: 'Performance improvement program is inadequate.',
      unknown: 'Review PI program against 0720-27-.06(9).',
    },
    stateNotes: [
      'Committee reviews services at least annually per 0720-27-.06(9)(a)',
      'Written plan required per 0720-27-.06(9)(a)',
      'Includes contract services per 0720-27-.06(9)(a)',
    ],
  },

  {
    id: 'TN-A5',
    itemId: 'A5',
    stateCode: 'TN',
    title: 'Disaster Preparedness',
    description: 'Tennessee requires emergency communications with Tennessee Emergency Management Agency per 0720-27-.14',
    severity: 'medium',
    regulationCode: '0720-27-.14',
    regulationName: 'Disaster Preparedness',
    agencyName: 'Tennessee Emergency Management Agency',
    agencyAbbr: 'TEMA',
    agencyPhone: '(615) 741-0001',
    agencyUrl: 'https://www.tn.gov/tema',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain communications with local Tennessee Emergency Management Agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - documented communications on file' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 0720-27-.14(1): Establish and maintain communications with local TEMA office.',
      },
      {
        id: 'q2',
        question: 'Do you cooperate in area disaster drills and local emergency situations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.14(1): Agency shall cooperate to extent possible.',
      },
      {
        id: 'q3',
        question: 'Do you maintain a file of documents demonstrating TEMA communications and cooperation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.14(2): File of documents must be maintained.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // Not cooperating in disaster drills per 0720-27-.14(1)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Disaster preparedness meets 0720-27-.14 requirements.',
      atRisk: 'Disaster preparedness documentation may be incomplete or not cooperating in disaster drills per 0720-27-.14(1).',
      nonCompliant: 'No disaster preparedness communications documented.',
      unknown: 'Verify TEMA communications are established.',
    },
    stateNotes: [
      'Establish communications with local TEMA office per 0720-27-.14(1)',
      'Provide information for local comprehensive emergency plan',
      'File of communications must be maintained per 0720-27-.14(2)',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'TN-B1',
    itemId: 'B1',
    stateCode: 'TN',
    title: 'Criminal Background Check',
    description: 'Tennessee requires criminal background checks for direct care staff PRIOR to employment per TCA § 68-11-233',
    severity: 'high',
    regulationCode: 'TCA § 68-11-233',
    regulationName: 'Criminal Background Check of Applicants',
    agencyName: 'Tennessee Bureau of Investigation',
    agencyAbbr: 'TBI',
    agencyPhone: '(615) 744-4000',
    agencyUrl: 'https://www.tn.gov/tbi',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete criminal background checks for ALL direct care staff PRIOR to employment?',
        inputType: 'select',
        options: [
          { value: 'always_prior', label: 'Yes - always prior to employment' },
          { value: 'sometimes', label: 'Sometimes after employment starts' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'Per TCA § 68-11-233(a): Background check required PRIOR to employment.',
      },
      {
        id: 'q2',
        question: 'What type of background check do you conduct?',
        inputType: 'select',
        options: [
          { value: 'tbi_fbi', label: 'TBI and FBI fingerprint check' },
          { value: 'professional', label: 'Professional background screening organization' },
          { value: 'both', label: 'Both TBI/FBI and professional screening' },
          { value: 'none', label: 'None' },
        ],
        helpText: 'Per TCA § 68-11-233(a): Check conducted by TBI/FBI or professional screening organization.',
      },
      {
        id: 'q3',
        question: 'Do you verify references for all direct care applicants?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per TCA § 68-11-233(b)(3): Applicant must provide past work and personal references to be checked.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always_prior' },
        { condition: 'includes', questionId: 'q2', value: ['tbi_fbi', 'professional', 'both'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Not verifying references per TCA § 68-11-233(b)(3)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet TCA § 68-11-233 requirements.',
      atRisk: 'Background checks must be completed PRIOR to employment and references must be verified per TCA § 68-11-233(a)(b)(3).',
      nonCompliant: 'Direct care staff working without required background checks. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Verify background check procedures comply with TCA § 68-11-233.',
    },
    stateNotes: [
      'Background check required PRIOR to employment per TCA § 68-11-233(a)',
      'Options: TBI/FBI fingerprints OR professional screening organization',
      'Applicant must consent to release of records per TCA § 68-11-233(b)(1)',
      'Applicant must provide references per TCA § 68-11-233(b)(3)',
      'Staffing agencies must also comply per TCA § 68-11-233(e)',
    ],
  },

  {
    id: 'TN-B2',
    itemId: 'B2',
    stateCode: 'TN',
    title: 'Abuse Registry Check',
    description: 'Tennessee requires checking the Abuse Registry per TCA § 68-11-1004',
    severity: 'high',
    regulationCode: 'TCA § 68-11-1004',
    regulationName: 'Abuse Registry',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(877) 287-0010',
    agencyUrl: 'https://apps.health.tn.gov/AbuseRegistry/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all potential direct care employees against the Tennessee Abuse Registry before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before hire' },
          { value: 'sometimes', label: 'Sometimes, but not consistently' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per TCA § 68-11-1004: No employee on registry may provide services to vulnerable persons.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse registry verification is conducted for all hires.',
      atRisk: 'Abuse registry checks not consistently conducted.',
      nonCompliant: 'Failing to check abuse registry violates TCA § 68-11-1004.',
      unknown: 'Verify abuse registry check procedures.',
    },
    stateNotes: [
      'Registry maintained by THFC per TCA § 68-11-1001',
      'Names of persons who have abused, neglected, exploited, or misappropriated property',
      'No person on registry may provide services to vulnerable persons per TCA § 68-11-1004',
      'Registry available at: https://apps.health.tn.gov/AbuseRegistry/',
    ],
  },

  {
    id: 'TN-B3',
    itemId: 'B3',
    stateCode: 'TN',
    title: 'OIG/SAM Exclusion Verification',
    description: 'Federal requirement to verify staff not excluded from federal programs',
    severity: 'high',
    regulationCode: '42 USC § 1320a-7',
    regulationName: 'OIG Exclusion Verification',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How frequently do you check OIG/SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No checks' },
        ],
        required: true,
        helpText: 'OIG recommends monthly screening for all employees.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion verification meets federal requirements.',
      atRisk: 'OIG checks should be conducted monthly, not just at hire.',
      nonCompliant: 'Required OIG exclusion checks not being conducted.',
      unknown: 'Verify OIG/SAM screening procedures.',
    },
    stateNotes: [
      'OIG LEIE check recommended monthly',
      'SAM.gov check for federal debarment',
      'Employing excluded individual can result in civil monetary penalties',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL RECORDS
  // ============================================
  {
    id: 'TN-C1',
    itemId: 'C1',
    stateCode: 'TN',
    title: 'Personnel File Requirements',
    description: 'Tennessee requires specific personnel documentation per 0720-27-.04(11-12)',
    severity: 'high',
    regulationCode: '0720-27-.04(11-12)',
    regulationName: 'Personnel',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'license', label: 'Current license/license number and renewal number - 0720-27-.04(11)' },
          { value: 'education', label: 'Education, training, experience - 0720-27-.04(11)' },
          { value: 'background', label: 'Personnel background information - 0720-27-.04(11)' },
          { value: 'health', label: 'Adequate medical screenings (exclude communicable disease) - 0720-27-.04(11)' },
          { value: 'job_desc', label: 'Job descriptions - 0720-27-.04(12)' },
          { value: 'references', label: 'Verification of references and credentials - 0720-27-.04(12)' },
          { value: 'evaluations', label: 'Performance evaluations - 0720-27-.04(12)' },
          { value: 'orientation', label: 'Orientation documentation - 0720-27-.04(13)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.04(11-12): Personnel records must be kept current.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 0720-27-.04.',
      atRisk: 'Some personnel files may be missing required documents.',
      nonCompliant: 'Personnel files missing critical documentation.',
      unknown: 'Audit personnel files against 0720-27-.04(11-12).',
    },
    stateNotes: [
      'Licensure documentation required for licensed positions per 0720-27-.04(11)',
      'Medical screenings to exclude communicable disease per 0720-27-.04(11)',
      'Personnel records must be kept current per 0720-27-.04(12)',
    ],
  },

  {
    id: 'TN-C2',
    itemId: 'C2',
    stateCode: 'TN',
    title: 'Staff Education Program',
    description: 'Tennessee requires ongoing educational program per 0720-27-.04(13)',
    severity: 'medium',
    regulationCode: '0720-27-.04(13)',
    regulationName: 'Staff Development',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Does your staff education program include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'orientation_org', label: 'Orientation to the organization - 0720-27-.04(13)' },
          { value: 'orientation_policies', label: 'Orientation to policies - 0720-27-.04(13)' },
          { value: 'orientation_position', label: 'Orientation to position and duties - 0720-27-.04(13)' },
          { value: 'ongoing', label: 'Ongoing educational program for skill development - 0720-27-.04(13)' },
          { value: 'records', label: 'Records of subject and attendance maintained - 0720-27-.04(13)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.04(13): Ongoing educational program planned and conducted for all staff.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff education program meets 0720-27-.04(13) requirements.',
      atRisk: 'Staff education program may be incomplete.',
      nonCompliant: 'Staff education program is inadequate.',
      unknown: 'Review education program against requirements.',
    },
    stateNotes: [
      'Each employee receives appropriate orientation per 0720-27-.04(13)',
      'Records of subject and attendance maintained per 0720-27-.04(13)',
      'For all personnel engaged in delivery of home health services',
    ],
  },

  // ============================================
  // SECTION D: TRAINING - HOME HEALTH AIDES
  // ============================================
  {
    id: 'TN-D1',
    itemId: 'D1',
    stateCode: 'TN',
    title: 'Home Health Aide Training',
    description: 'Tennessee requires 75 hours training for home health aides per 0720-27-.01(29)',
    severity: 'high',
    regulationCode: '0720-27-.01(29), 0720-27-.06(7)(a)',
    regulationName: 'Home Health Aide Training',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Have ALL home health aides completed required training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - 75 hours total (16 hours clinical within first 3 months)' },
          { value: 'most', label: 'Most have completed training' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Per 0720-27-.01(29): 75 hours total training including 16 hours clinical prior to or during first 3 months.',
      },
      {
        id: 'q2',
        question: 'Does home health aide training cover required topics?',
        inputType: 'checklist',
        options: [
          { value: 'nutrition', label: 'Methods of assisting with nutrition/meal preparation - 0720-27-.06(7)(a)' },
          { value: 'aging', label: 'Aging process and emotional problems of illness - 0720-27-.06(7)(a)' },
          { value: 'environment', label: 'Maintaining clean, healthy, pleasant environment - 0720-27-.06(7)(a)' },
          { value: 'changes', label: 'Changes in patient condition to report - 0720-27-.06(7)(a)' },
          { value: 'teamwork', label: 'Work of agency and health team - 0720-27-.06(7)(a)' },
          { value: 'ethics', label: 'Ethics, confidentiality, respect for dignity - 0720-27-.06(7)(a)' },
          { value: 'records', label: 'Record keeping - 0720-27-.06(7)(a)' },
        ],
        helpText: 'Per 0720-27-.06(7)(a): Training must cover these specific areas.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training meets 0720-27-.01(29) requirements.',
      atRisk: 'Some aides may not have completed required 75-hour training.',
      nonCompliant: 'Aides providing services without required training.',
      unknown: 'Verify aide training documentation.',
    },
    stateNotes: [
      '75 hours total training per 0720-27-.01(29)',
      '16 hours clinical training prior to or during first 3 months',
      'Must comply with federal home health aide training regulations per 0720-27-.06(7)(a)',
      'RN assigns aide to particular patient per 0720-27-.06(7)(b)',
    ],
  },

  {
    id: 'TN-D2',
    itemId: 'D2',
    stateCode: 'TN',
    title: 'Continuing In-Service Training',
    description: 'Tennessee requires continuing in-service programs for home health aides per 0720-27-.06(7)(d)',
    severity: 'medium',
    regulationCode: '0720-27-.06(7)(d)',
    regulationName: 'Aide In-Service Training',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide continuing in-service programs for home health aides?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes - regularly scheduled with on-the-job training' },
          { value: 'occasional', label: 'Occasionally' },
          { value: 'no', label: 'No formal in-service program' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(7)(d): Continuing in-service programs on regularly scheduled basis.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Aide in-service training meets 0720-27-.06(7)(d) requirements.',
      atRisk: 'In-service training should be on regularly scheduled basis.',
      nonCompliant: 'No formal in-service training program for aides.',
      unknown: 'Verify in-service training program.',
    },
    stateNotes: [
      'Continuing in-service on regularly scheduled basis per 0720-27-.06(7)(d)',
      'On-the-job training during supervisor visits per 0720-27-.06(7)(d)',
      'More often as needed per 0720-27-.06(7)(d)',
    ],
  },

  // ============================================
  // SECTION E: SUPERVISION
  // ============================================
  {
    id: 'TN-E1',
    itemId: 'E1',
    stateCode: 'TN',
    title: 'Home Health Aide Supervision',
    description: 'Tennessee requires monthly RN supervisory visits per 0720-27-.06(7)(c)',
    severity: 'high',
    regulationCode: '0720-27-.06(7)(c)',
    regulationName: 'Aide Supervision',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'How often does an RN or appropriate professional staff make supervisory visits to patient residences?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'At least monthly per 0720-27-.06(7)(c)' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'rarely', label: 'Rarely or as needed' },
          { value: 'never', label: 'No regular supervisory visits' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(7)(c): RN or appropriate staff makes supervisory visit at least monthly.',
      },
      {
        id: 'q2',
        question: 'Do supervisory visits alternate between when aide is present vs. absent?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - preferably alternating per regulation' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.06(7)(c): Either when aide is present to observe and assist, or when absent (preferably alternating).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'quarterly', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['rarely', 'never'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervisory visits meet monthly requirement per 0720-27-.06(7)(c).',
      atRisk: 'Supervisory visits must be at least monthly.',
      nonCompliant: 'Inadequate supervision of home health aides.',
      unknown: 'Verify supervision frequency.',
    },
    stateNotes: [
      'RN or appropriate professional staff per 0720-27-.06(7)(c)',
      'At least monthly to patient residence',
      'Either when aide present (to observe/assist) or absent (preferably alternating)',
      'Assess aide competence and determine if goals being met',
    ],
  },

  {
    id: 'TN-E2',
    itemId: 'E2',
    stateCode: 'TN',
    title: 'Skilled Nursing Services Supervision',
    description: 'Tennessee requires RN supervision of skilled nursing services per 0720-27-.06(5)',
    severity: 'high',
    regulationCode: '0720-27-.06(5)',
    regulationName: 'Skilled Nursing Services',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Is there an RN available at all times during operating hours to supervise skilled nursing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(5)(a): RN shall be available at all times during operating hours.',
      },
      {
        id: 'q2',
        question: 'Does the RN have no current disciplinary action against license?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - no disciplinary action' },
          { value: 'no', label: 'No / unsure' },
        ],
        helpText: 'Per 0720-27-.06(5)(a): RN must have no current disciplinary action against license.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyFalse', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets 0720-27-.06(5) requirements.',
      atRisk: 'RN supervision may not meet requirements.',
      nonCompliant: 'Skilled nursing services not properly supervised by qualified RN.',
      unknown: 'Verify RN supervision.',
    },
    stateNotes: [
      'RN available at all times during operating hours per 0720-27-.06(5)(a)',
      'RN must have no current disciplinary action per 0720-27-.06(5)(a)',
      'RN participates in all activities relevant to professional services per 0720-27-.06(5)(a)',
      'RN makes initial evaluation visit (unless only therapy ordered) per 0720-27-.06(5)(b)',
    ],
  },

  // ============================================
  // SECTION F: PLAN OF CARE
  // ============================================
  {
    id: 'TN-F1',
    itemId: 'F1',
    stateCode: 'TN',
    title: 'Plan of Care Requirements',
    description: 'Tennessee requires physician-established plan of care reviewed every 62 days per 0720-27-.06(3)',
    severity: 'high',
    regulationCode: '0720-27-.06(3)',
    regulationName: 'Plan of Care',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Per 0720-27-.05(2): Care shall follow a written plan of care established and reviewed by a physician.',
      },
      {
        id: 'q2',
        question: 'Do your plans of care include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'diagnoses', label: 'Pertinent diagnoses including mental status - 0720-27-.06(3)(a)' },
          { value: 'services', label: 'Types of services and equipment required - 0720-27-.06(3)(a)' },
          { value: 'frequency', label: 'Frequency of services - 0720-27-.06(3)(a)' },
          { value: 'prognosis', label: 'Prognosis and rehabilitation potential - 0720-27-.06(3)(a)' },
          { value: 'limitations', label: 'Functional limitations - 0720-27-.06(3)(a)' },
          { value: 'activities', label: 'Activities permitted - 0720-27-.06(3)(a)' },
          { value: 'nutrition', label: 'Nutritional requirements - 0720-27-.06(3)(a)' },
          { value: 'medications', label: 'Medications and treatments - 0720-27-.06(3)(a)' },
          { value: 'safety', label: 'Safety measures to protect against injury - 0720-27-.06(3)(a)' },
          { value: 'discharge', label: 'Instructions for timely discharge or referral - 0720-27-.06(3)(a)' },
        ],
        helpText: 'Per 0720-27-.06(3)(a): Plan of care must cover all listed elements.',
      },
      {
        id: 'q3',
        question: 'Is the plan of care reviewed by attending physician and agency at least every 62 days?',
        inputType: 'select',
        options: [
          { value: 'per_regs', label: 'Yes - every 62 days or more frequently as condition requires' },
          { value: 'less_frequent', label: 'Less frequently than every 62 days' },
          { value: 'no', label: 'No formal review process' },
        ],
        helpText: 'Per 0720-27-.06(3)(b): Reviewed at least once every 62 days with physician signature and date.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'per_regs' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q3', value: 'less_frequent' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Plan of care missing required elements per 0720-27-.06(3)(a)
      { condition: 'countAtLeast', questionId: 'q2', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care procedures meet 0720-27-.06(3) requirements.',
      atRisk: 'Plan of care may not meet 62-day review requirement or may be missing required elements per 0720-27-.06(3)(a).',
      nonCompliant: 'Services provided without required plan of care or plan missing critical elements.',
      unknown: 'Review plan of care procedures.',
    },
    stateNotes: [
      'Written plan developed in consultation with organization staff per 0720-27-.06(3)(a)',
      'Reviewed at least every 62 days per 0720-27-.06(3)(b)',
      'Physician signature and date required for review per 0720-27-.06(3)(b)',
      'Facsimile of physician signature acceptable per 0720-27-.06(3)(b)',
      'Written summary report to physician at least every 62 days per 0720-27-.06(2)',
    ],
  },

  // ============================================
  // SECTION G: CLINICAL RECORDS
  // ============================================
  {
    id: 'TN-G1',
    itemId: 'G1',
    stateCode: 'TN',
    title: 'Medical Record Requirements',
    description: 'Tennessee requires comprehensive medical records per 0720-27-.06(11)',
    severity: 'high',
    regulationCode: '0720-27-.06(11)',
    regulationName: 'Medical Records',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Do your medical records include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'identifying', label: 'Appropriate identifying information - 0720-27-.06(11)(a)' },
          { value: 'physician', label: 'Name of physician - 0720-27-.06(11)(a)' },
          { value: 'poc', label: 'Plan of care - 0720-27-.06(11)(a)' },
          { value: 'medications', label: 'All medications and treatments - 0720-27-.06(11)(a)' },
          { value: 'clinical_notes', label: 'Signed and dated clinical notes - 0720-27-.06(11)(a)' },
          { value: 'summary', label: 'Copies of summary reports sent to physician - 0720-27-.06(11)(a)' },
          { value: 'discharge', label: 'Discharge summary dated and signed within 7 days - 0720-27-.06(11)(a)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(11)(a): Medical record maintained for every patient receiving services.',
      },
      {
        id: 'q2',
        question: 'Are clinical notes written the day service is rendered and incorporated no less often than weekly?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.06(11)(a): Clinical notes written same day and incorporated no less than weekly.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medical records meet 0720-27-.06(11) requirements.',
      atRisk: 'Medical records may be missing required elements.',
      nonCompliant: 'Medical records are incomplete.',
      unknown: 'Audit medical records against 0720-27-.06(11).',
    },
    stateNotes: [
      'Record maintained for every patient per 0720-27-.06(11)(a)',
      'Clinical notes written same day, incorporated weekly per 0720-27-.06(11)(a)',
      'Discharge summary within 7 days per 0720-27-.06(11)(a)',
      'Electronic signed orders acceptable per 0720-27-.06(11)(b)',
    ],
  },

  {
    id: 'TN-G2',
    itemId: 'G2',
    stateCode: 'TN',
    title: 'Record Retention',
    description: 'Tennessee requires 10-year minimum record retention per 0720-27-.06(11)(c)',
    severity: 'high',
    regulationCode: '0720-27-.06(11)(c)',
    regulationName: 'Record Retention',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain medical records?',
        inputType: 'select',
        options: [
          { value: '10plus', label: '10 years or longer (meets TN requirement)' },
          { value: '7to9', label: '7-9 years' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(11)(c): Minimum 10 years retention.',
      },
      {
        id: 'q2',
        question: 'For minors or mentally disabled patients, do you retain records for period of minority/disability + 1 year, or 10 years, whichever is longer?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - no such patients' },
        ],
        helpText: 'Per 0720-27-.06(11)(c): Period of minority/disability + 1 year, or 10 years, whichever longer.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '10plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '7to9', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['5to6', 'less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets 10-year requirement per 0720-27-.06(11)(c).',
      atRisk: 'Tennessee requires 10-year retention, not 7. Update your policy.',
      nonCompliant: 'Record retention does not meet Tennessee 10-year requirement.',
      unknown: 'Review record retention policy against 0720-27-.06(11)(c).',
    },
    stateNotes: [
      '10 years minimum per 0720-27-.06(11)(c)',
      'Minors/mentally disabled: period of minority/disability + 1 year, OR 10 years, whichever longer',
      'Records destruction by burning, shredding, or other effective method per 0720-27-.06(11)(c)',
      'Must document destruction per 0720-27-.06(11)(c)',
      'If agency discontinues, records still maintained per Tennessee Medical Records Act (TCA § 68-11-308)',
    ],
  },

  {
    id: 'TN-G3',
    itemId: 'G3',
    stateCode: 'TN',
    title: 'Records and Reports to THFC',
    description: 'Tennessee requires reporting incidents and maintaining survey records per 0720-27-.11',
    severity: 'high',
    regulationCode: '0720-27-.11',
    regulationName: 'Records and Reports',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(877) 287-0010',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Do you submit the annual Joint Annual Report to the Department?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 0720-27-.11(1): Yearly statistical report required.',
      },
      {
        id: 'q2',
        question: 'Do you report required incidents to THFC?',
        inputType: 'checklist',
        options: [
          { value: 'abuse', label: 'All incidents of abuse, neglect, misappropriation - 0720-27-.11(2)' },
          { value: 'strike', label: 'Staff strike - 0720-27-.11(3)(a)' },
          { value: 'disaster', label: 'External disasters impacting agency - 0720-27-.11(3)(b)' },
          { value: 'disruption', label: 'Disruption of vital services - 0720-27-.11(3)(c)' },
          { value: 'fire', label: 'Fires disrupting services or causing harm - 0720-27-.11(3)(d)' },
        ],
        helpText: 'Per 0720-27-.11(2-3): Report these incidents to Department of Health per TCA § 68-11-211.',
      },
      {
        id: 'q3',
        question: 'Do you maintain survey/inspection records for 36 months and make available for public inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.11(4): Records maintained 36 months, available for inspection during normal business hours.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyFalse', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'AT_RISK' },
      // Incident reporting per 0720-27-.11(2-3)
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Reporting requirements meet 0720-27-.11.',
      atRisk: 'Some reporting/record keeping requirements may not be met. Review incident reporting per 0720-27-.11(2-3).',
      nonCompliant: 'Not meeting mandatory reporting requirements.',
      unknown: 'Verify reporting procedures.',
    },
    stateNotes: [
      'Annual Joint Annual Report per 0720-27-.11(1)',
      'Incident reporting per TCA § 68-11-211',
      'Survey records retained 36 months per 0720-27-.11(4)',
      'Records available for public inspection per 0720-27-.11(4)',
    ],
  },

  // ============================================
  // SECTION H: ABUSE REPORTING
  // ============================================
  {
    id: 'TN-H1',
    itemId: 'H1',
    stateCode: 'TN',
    title: 'Mandatory Abuse Reporting',
    description: 'Tennessee requires immediate reporting of suspected abuse per TCA § 71-6-103',
    severity: 'high',
    regulationCode: 'TCA § 71-6-103, 0720-27-.12(1)(d)',
    regulationName: 'Adult Protection Act',
    agencyName: 'Tennessee Department of Human Services - Adult Protective Services',
    agencyAbbr: 'DHS-APS',
    agencyPhone: '1-888-APS-TENN (1-888-277-8366)',
    agencyUrl: 'https://www.tn.gov/humanservices/adults/adult-protective-services.html',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL staff trained on mandatory abuse reporting requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all staff trained' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
        helpText: 'Per TCA § 71-6-103(b): Any person with reasonable cause to suspect abuse shall report immediately.',
      },
      {
        id: 'q2',
        question: 'Do staff know to report suspected abuse to DHS Adult Protective Services immediately?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - report immediately to 1-888-APS-TENN' },
          { value: 'no', label: 'No / not sure' },
        ],
        helpText: 'Per TCA § 71-6-103(c): Oral or written report made immediately to department.',
      },
      {
        id: 'q3',
        question: 'Do you notify THFC within 5 business days when patient rights are violated (including abuse)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 0720-27-.12(1)(d): Notify Department within 5 business days and DHS Adult Protective Services per TCA §§ 71-6-101',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse reporting training and procedures meet Tennessee requirements.',
      atRisk: 'Some staff may lack abuse reporting training.',
      nonCompliant: 'Staff not trained on mandatory abuse reporting. Failure to report is Class A misdemeanor.',
      unknown: 'Verify abuse reporting training and procedures.',
    },
    stateNotes: [
      'Any person with reasonable cause must report immediately per TCA § 71-6-103(b)',
      'Report to DHS Adult Protective Services: 1-888-APS-TENN (1-888-277-8366)',
      'Online reporting: https://reportadultabuse.dhs.tn.gov/',
      'Also notify THFC within 5 business days per 0720-27-.12(1)(d)',
      'Failure to report: Class A misdemeanor, up to 11 months 29 days or fine up to $2,500 per TCA § 71-6-103',
      'Reporter immunity per TCA § 71-6-105',
    ],
  },

  // ============================================
  // SECTION I: PATIENT RIGHTS
  // ============================================
  {
    id: 'TN-I1',
    itemId: 'I1',
    stateCode: 'TN',
    title: 'Patient Rights',
    description: 'Tennessee requires patients have specific rights per 0720-27-.12',
    severity: 'high',
    regulationCode: '0720-27-.12',
    regulationName: 'Patient Rights',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(877) 287-0010',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency protect all required patient rights?',
        inputType: 'checklist',
        options: [
          { value: 'privacy', label: 'Privacy in treatment and personal care - 0720-27-.12(1)(a)' },
          { value: 'pain', label: 'Appropriate assessment and management of pain - 0720-27-.12(1)(b)' },
          { value: 'involvement', label: 'Involved in decision making of all aspects of care - 0720-27-.12(1)(c)' },
          { value: 'free_abuse', label: 'Free from mental and physical abuse - 0720-27-.12(1)(d)' },
          { value: 'refuse_treatment', label: 'Refuse treatment (with informed consequences) - 0720-27-.12(1)(e)' },
          { value: 'refuse_experimental', label: 'Refuse experimental treatment and drugs - 0720-27-.12(1)(f)' },
          { value: 'confidentiality', label: 'Records kept confidential and private - 0720-27-.12(1)(g)' },
          { value: 'self_determination', label: 'Self-determination including advance directives - 0720-27-.12(2)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.12: Each patient has at least these rights.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights protections meet 0720-27-.12 requirements.',
      atRisk: 'Some patient rights may not be fully protected.',
      nonCompliant: 'Patient rights not adequately protected.',
      unknown: 'Review patient rights against 0720-27-.12.',
    },
    stateNotes: [
      'Abuse violation requires notifying THFC within 5 business days and DHS APS per 0720-27-.12(1)(d)',
      'Treatment refusal must be informed of consequences and documented per 0720-27-.12(1)(e)',
      'Written consent required for research per 0720-27-.12(1)(f)',
      'Written consent required for release of records per 0720-27-.12(1)(g)',
    ],
  },

  // ============================================
  // SECTION J: INFECTION CONTROL
  // ============================================
  {
    id: 'TN-J1',
    itemId: 'J1',
    stateCode: 'TN',
    title: 'Infection Control Program',
    description: 'Tennessee requires active infection control program per 0720-27-.06(10)',
    severity: 'high',
    regulationCode: '0720-27-.06(10)',
    regulationName: 'Infection Control',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Does your infection control program include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'guidelines', label: 'Guidelines, policies, procedures for prevention/control - 0720-27-.06(10)(a)' },
          { value: 'education', label: 'Education on aseptic techniques for staff/family - 0720-27-.06(10)(b)' },
          { value: 'continuing', label: 'Continuing education on cause, transmission, prevention - 0720-27-.06(10)(c)' },
          { value: 'influenza', label: 'Annual influenza vaccination program - 0720-27-.06(10)(d)' },
          { value: 'hepatitis', label: 'Policies for hepatitis B/HIV testing upon exposure - 0720-27-.06(10)(e)' },
          { value: 'standard', label: 'Standard precautions (per CDC) - 0720-27-.06(10)(f)' },
          { value: 'sterile', label: 'Prevention of contamination of sterile supplies - 0720-27-.06(10)(g)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(10): Active performance improvement program for infection control required.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 0720-27-.06(10) requirements.',
      atRisk: 'Infection control program may be incomplete.',
      nonCompliant: 'Infection control program is inadequate.',
      unknown: 'Review infection control against 0720-27-.06(10).',
    },
    stateNotes: [
      'Active PI program for prevention, control, investigation per 0720-27-.06(10)(a)',
      'Education on handwashing, hygiene, PPE, dressing care, etc. per 0720-27-.06(10)(b)',
      'Annual influenza vaccination program with declination per 0720-27-.06(10)(d)',
      'Standard precautions per CDC per 0720-27-.06(10)(f)',
    ],
  },

  {
    id: 'TN-J2',
    itemId: 'J2',
    stateCode: 'TN',
    title: 'Annual Influenza Vaccination Program',
    description: 'Tennessee requires annual influenza vaccination program per 0720-27-.06(10)(d)',
    severity: 'medium',
    regulationCode: '0720-27-.06(10)(d)',
    regulationName: 'Influenza Vaccination',
    agencyName: 'Tennessee Health Facilities Commission',
    agencyAbbr: 'THFC',
    agencyPhone: '(615) 741-7221',
    agencyUrl: 'https://www.tn.gov/healthfacilities',
    questions: [
      {
        id: 'q1',
        question: 'Does your influenza vaccination program include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'offer', label: 'Offer vaccination to all staff at no cost - 0720-27-.06(10)(d)(1)' },
          { value: 'declination', label: 'Signed declination statement from those who refuse - 0720-27-.06(10)(d)(2)' },
          { value: 'education', label: 'Education on flu vaccination, non-vaccine measures, impact - 0720-27-.06(10)(d)(3)' },
          { value: 'evaluation', label: 'Annual evaluation of program and non-participation reasons - 0720-27-.06(10)(d)(4)' },
          { value: 'shortage', label: 'Suspension provision for vaccine shortage - 0720-27-.06(10)(d)(5)' },
        ],
        required: true,
        helpText: 'Per 0720-27-.06(10)(d): Annual program must include all listed elements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Influenza vaccination program meets 0720-27-.06(10)(d) requirements.',
      atRisk: 'Influenza program may be missing required elements.',
      nonCompliant: 'Influenza vaccination program is inadequate.',
      unknown: 'Review flu program against requirements.',
    },
    stateNotes: [
      'Vaccination offered at no cost per 0720-27-.06(10)(d)(1)',
      'Signed declination for refusals (not medical contraindication) per 0720-27-.06(10)(d)(2)',
      'Sample form available at http://tennessee.gov/health/topic/hcf-provider',
      'Administrator may suspend requirements during shortage per 0720-27-.06(10)(d)(5)',
    ],
  },
];

export default TENNESSEE_QUESTIONS;